/**
 * Environment-agnostic zyBooks HTTP client shared by the browser app and the
 * tiptap-to-zybooks-json Lambda. Must not import browser-only modules.
 */

export interface AuthProvider {
    getAuthToken(): string | null;
    /** Returns true when a new token is available. Omit to fail fast on auth errors. */
    refreshAuthToken?(): Promise<boolean>;
}

export interface RequestOptions {
    headers?: Record<string, string>;
    body?: any;
    params?: Record<string, any>;
    maxAttempts?: number;
    pollInterval?: number;
    /** When false, a 202 response resolves with its body (including `location`) instead of polling the task. Defaults to true. */
    pollAccepted?: boolean;
    skipAuthRetry?: boolean;
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface PollingResponse {
    location?: string;
    state?: 'SUCCESS' | 'PENDING' | 'RETRY';
    [key: string]: any;
}

interface ApiResponse {
    success?: boolean;
    error?: {
        code?: number;
        message?: string;
    };
}

const AUTH_ERROR_CODES = new Set([401, 430]);
const POLL_REQUEST_TIMEOUT_MS = 30000;

function isAuthErrorPayload(data: ApiResponse): boolean {
    return data.success === false && AUTH_ERROR_CODES.has(data.error?.code ?? -1);
}

/**
 * Joins a base URL and path with exactly one slash between them.
 * @param baseUrl - Server base URL, with or without a trailing slash
 * @param path - Endpoint path, with or without a leading slash
 * @returns The absolute URL
 */
export function joinUrl(baseUrl: string, path: string): string {
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${normalizedBase}${normalizedPath}`;
}

/**
 * Creates an auth provider for a fixed token that cannot be refreshed.
 * @param token - Bearer token (without the "Bearer " prefix)
 * @returns Auth provider that always returns the token
 */
export function staticTokenAuth(token: string): AuthProvider {
    return { getAuthToken: () => token };
}

/**
 * Polls a location URL until the task is complete
 * @param location - The URL to poll
 * @param authToken - The authentication token
 * @param maxAttempts - The maximum number of attempts
 * @param pollInterval - The interval between attempts
 * @returns The final response when polling completes
 */
async function pollUntilComplete<T = any>(
    location: string,
    authToken: string,
    maxAttempts: number = 60,
    pollInterval: number = 1000,
): Promise<T> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        console.log(`Polling attempt ${attempt + 1}/${maxAttempts}...`);
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), POLL_REQUEST_TIMEOUT_MS);

        let statusResponse: Response;
        try {
            statusResponse = await fetch(location, {
                headers: { Authorization: `Bearer ${authToken}` },
                signal: controller.signal,
            });
        } finally {
            clearTimeout(timeoutId);
        }

        if (statusResponse.status !== 200) {
            throw new Error(`Unexpected status code ${statusResponse.status} while checking task status`);
        }

        const result = (await statusResponse.json()) as PollingResponse & T;
        if (result.state === 'PENDING' || result.state === 'RETRY') {
            continue;
        }
        // SUCCESS and any other terminal state are returned as-is.
        return result as T;
    }

    throw new Error('Task did not complete within timeout period');
}

async function pollAcceptedResponse<T>(
    response: Response,
    authToken: string | null,
    maxAttempts?: number,
    pollInterval?: number,
): Promise<T> {
    const responseData: PollingResponse = await response.json();
    const location = responseData.location;

    if (!location) {
        throw new Error('No location provided in 202 response');
    }

    if (!authToken) {
        throw new Error('Not authenticated');
    }
    return pollUntilComplete<T>(location, authToken, maxAttempts, pollInterval);
}

function hasBody(method: HttpMethod, body: unknown): boolean {
    return method !== 'GET' && body !== undefined;
}

async function makeRequest<T = any>(
    baseUrl: string,
    endpoint: string,
    method: HttpMethod,
    auth: AuthProvider,
    options: RequestOptions = {},
): Promise<T> {
    let url = joinUrl(baseUrl, endpoint);
    const { body, params, maxAttempts, pollInterval, pollAccepted = true } = options;
    let headers = options.headers;

    // Caller-supplied headers fully replace the defaults, including Authorization.
    if (!headers) {
        headers = { Authorization: `Bearer ${auth.getAuthToken()}` };
        if (hasBody(method, body)) {
            headers['Content-Type'] = 'application/json';
        }
    }

    const fetchOptions: RequestInit = { method, headers };

    if (hasBody(method, body)) {
        fetchOptions.body = JSON.stringify(body);
    }

    if (method === 'GET' && params !== undefined) {
        const urlObj = new URL(url);
        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                return;
            }
            const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
            urlObj.searchParams.append(key, stringValue);
        });
        url = urlObj.toString();
    }

    const canRetryAuth = !options.skipAuthRetry && auth.refreshAuthToken !== undefined;

    for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) {
            const refreshed = await auth.refreshAuthToken!();
            if (!refreshed) {
                throw new Error('Session expired');
            }

            fetchOptions.headers = {
                ...(fetchOptions.headers as Record<string, string>),
                Authorization: `Bearer ${auth.getAuthToken()}`,
            };
        }

        const shouldRetryAuth = canRetryAuth && attempt === 0;
        const response = await fetch(url, fetchOptions);

        if (response.status === 202) {
            if (!pollAccepted) {
                return (await response.json()) as T;
            }
            return pollAcceptedResponse<T>(response, auth.getAuthToken(), maxAttempts, pollInterval);
        }

        if (!response.ok) {
            if (response.status === 401 && shouldRetryAuth) {
                continue;
            }
            throw new Error(`Request failed with status ${response.status} for ${endpoint}`);
        }

        const responseData: T & ApiResponse = await response.json();

        if (isAuthErrorPayload(responseData) && shouldRetryAuth) {
            continue;
        }

        if (responseData.success === false) {
            throw new Error(responseData.error?.message ?? 'Unknown error');
        }

        return responseData;
    }

    throw new Error('Session expired');
}

/**
 * Creates a zyBooks server client bound to a base URL and auth provider.
 * @param baseUrl - The base URL for the server
 * @param auth - Supplies (and optionally refreshes) the bearer token
 * @returns Server client with get, post, put, patch and delete methods
 */
export function createServerClient(baseUrl: string, auth: AuthProvider) {
    return {
        get<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
            return makeRequest<T>(baseUrl, endpoint, 'GET', auth, options);
        },
        post<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
            return makeRequest<T>(baseUrl, endpoint, 'POST', auth, options);
        },
        put<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
            return makeRequest<T>(baseUrl, endpoint, 'PUT', auth, options);
        },
        patch<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
            return makeRequest<T>(baseUrl, endpoint, 'PATCH', auth, options);
        },
        delete<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
            return makeRequest<T>(baseUrl, endpoint, 'DELETE', auth, options);
        },
    };
}

export type ServerClient = ReturnType<typeof createServerClient>;

export interface ZyServers {
    zyAuthor: ServerClient;
    zyServer: ServerClient;
    zyServer2: ServerClient;
    itLabsServer: ServerClient;
    zyStreamlit: ServerClient;
}
